import { type ChangeEvent, type ReactNode } from 'react';
import logoIcon from '@/assets/logo-icon.svg';
import { GlowOrb } from '@/components/effects/glow-orb';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SpinnerInline } from '@/components/ui/spinner';
import {
  useChangePassword,
  type ChangePasswordState,
} from './use-change-password';

const GLASS = 'border-white/[0.08] bg-white/[0.05] backdrop-blur-xl';

type FieldProps = {
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  autoComplete: string;
};

function PwField(p: FieldProps): ReactNode {
  return <Input {...p} type="password" required className={GLASS} />;
}

function Header(): ReactNode {
  return (
    <div className="mb-lg text-center">
      <img src={logoIcon} alt="Debook" className="mx-auto mb-md h-10 w-10" />
      <h1 className="font-display text-base font-medium text-text-primary">
        Cambiar contrase&ntilde;a
      </h1>
      <p className="mt-xs text-xs text-text-tertiary">
        Elige una nueva contrase&ntilde;a para continuar
      </p>
    </div>
  );
}

function Fields({ s }: { s: ChangePasswordState }): ReactNode {
  return (
    <>
      <PwField
        label="Contraseña actual"
        value={s.current}
        onChange={(e) => s.setCurrent(e.target.value)}
        autoComplete="current-password"
      />
      <PwField
        label="Nueva contraseña"
        value={s.next}
        onChange={(e) => s.setNext(e.target.value)}
        autoComplete="new-password"
      />
      <PwField
        label="Confirmar contraseña"
        value={s.confirm}
        onChange={(e) => s.setConfirm(e.target.value)}
        autoComplete="new-password"
      />
    </>
  );
}

export function ChangePasswordPage(): ReactNode {
  const s = useChangePassword();
  return (
    <div className="relative flex min-h-dvh items-center justify-center bg-void px-base">
      <GlowOrb />
      <div className="relative z-10 w-full max-w-[300px]">
        <Header />
        <form onSubmit={s.handleSubmit} className="space-y-md">
          <Fields s={s} />
          {s.error && <p className="text-xs text-error">{s.error}</p>}
          <Button type="submit" disabled={s.loading} className="mt-sm w-full">
            {s.loading ? <SpinnerInline /> : 'Guardar'}
          </Button>
        </form>
      </div>
    </div>
  );
}
